import React, { useState, useEffect } from 'react'
import styled, { withTheme } from 'styled-components'
import cn from 'classnames'
import { isUndefined } from 'lodash'
import Layout from '../layout'
import Icon from '../tntd-icon'
import Header from './Header'
import Logo from './Logo'
import SideMenu from './SideMenu'
import { getSideMenuStore, setSideMenuStore, getLanguageStore, getThemeStore } from './storage'

const { Content, Sider } = Layout
const TNTLayout = styled(Layout)`
  position: relative;
  .tnt-layout-sider {
    overflow-x: hidden;
    background: #191d29;
  }
  .tnt-layout-menu-collapse {
    position: absolute;
    bottom: 0;
    left: 0;
    line-height: 36px;
    background: #191d29;
    box-shadow: 0 0 6px 0 rgb(0 0 0 / 10%);
    color: #fff;
    width: 100%;
    padding-left: 24px;
    font-size: 14px;
    cursor: pointer;
  }
  &.isInIframe,
  &.isEmptyLayout {
    .tnt-layout-sider {
      display: none;
    }
  }
  &.compatible .tnt-layout-sider {
    background: linear-gradient(#213f83, #0f75d6) !important;
  }
  &.tnt-themeS1 {
    .tnt-layout-sider {
      background: #fff !important;
      overflow-x: unset;
    }
    .tnt-layout-menu-collapse {
      z-index: 3;
      background: #fff;
      color: ${(props) => props.theme.primaryTextColor};
    }
  }
  // 蓝色系列
  &.tnt-themeS3 {
    .tnt-layout-sider {
      background-image: linear-gradient(180deg, #213f83 0%, #0f75d6 100%) !important;
      overflow-x: unset;
    }
    .tnt-layout-menu-collapse {
      background: #207ad4;
      color: #fff;
    }
  }
  & > .ant-layout-content {
    background-color: ${(props) => props.theme.lightBgColor};
    & > div:nth-child(2) {
      height: calc(100vh - 50px);
      overflow: auto;
    }
  }
  &.large-size > .ant-layout-content {
    & > div:nth-child(2) {
      height: calc(100vh - 60px);
    }
  }
`

export default withTheme((props) => {
  const {
    type,
    logo,
    name,
    enName,
    selectedOrgKey,
    orgAppShow,
    orgList,
    orgAppList,
    onOrgChange,
    selectedAppKey,
    appList,
    selectedMenuKey,
    menuAlignMode = 'side',
    collapseIconPlacement = 'right',
    menus = [],
    formatMenuPath,
    changeRouter,
    headerTabs,
    orgTreeConfig,
    headerNavs,
    extraHeaderActions,
    isInIframe,
    isEmptyLayout,
    onLogoClick = () => null,
    onCollapseChange,
    onThemeChange,
    onLanguageChange,
    onBeforeMenuChange,
    onMenuChange,
    onMenuSelect,
    onAppChange,
    onPersonalSetting,
    onChangePassword,
    onLogout,
    config,
    extendMap = {},
    AvatarCustom, // 右上角用户信息自定义
  } = props
  let { collapsed: storeCollapsed, openKeys = [] } = getSideMenuStore()
  const [collapsed, setCollapsed] = useState(
    isUndefined(storeCollapsed) ? props.collapsed : storeCollapsed
  )
  const [openMenuKeys, setOpenMenuKeys] = useState(
    (() => {
      openKeys = collapsed ? [] : openKeys
      openKeys = openKeys instanceof Array ? openKeys : []
      return openKeys
    })(openKeys)
  )
  const [userInfo, setUserInfo] = useState(props.userInfo || {})
  let theme = getThemeStore() || extendMap?.defaultTheme || userInfo.theme || 'themeS2'
  const language = getLanguageStore() || userInfo.lang || 'cn'
  theme = theme === 'default' ? 'themeS2' : theme

  const collapseChangeHandle = () => {
    const { beforeOpenKeys = [] } = getSideMenuStore()
    const newCollapsed = !collapsed

    // 菜单收起：记住当前展开子菜单到store，并收起子菜单
    if (newCollapsed) {
      setSideMenuStore({
        collapsed: newCollapsed,
        beforeOpenKeys: openMenuKeys,
      })
      setOpenMenuKeys([])
    } else {
      // 菜单展开：恢复子菜单展开项，并更新到store
      setSideMenuStore({
        collapsed: newCollapsed,
        openKeys: beforeOpenKeys,
      })
      setOpenMenuKeys(beforeOpenKeys)
    }

    setCollapsed(newCollapsed)
    onCollapseChange && onCollapseChange(newCollapsed)
  }

  useEffect(() => {
    setUserInfo(props.userInfo)
  }, [props.userInfo])

  return (
    <TNTLayout
      className={cn(`tnt-layout tnt-${theme} ${props.className || ''}`, {
        'large-size': props.theme.size === 'large',
        compatible: props.theme.compatible,
        isInIframe,
        isEmptyLayout,
        noAppList: !appList,
        hasHeaderTabs: !!headerTabs,
        layoutMenusCollapsed: collapsed,
        collapseIconPlacementBottom: collapseIconPlacement === 'bottom',
      })}
    >
      <Sider
        collapsible
        breakpoint="md"
        className="tnt-layout-sider"
        collapsed={collapsed}
        width={220}
        trigger={null}
      >
        <Logo
          language={language}
          logo={logo}
          name={name}
          enName={enName}
          collapsed={collapsed}
          onClick={onLogoClick}
        />
        <SideMenu
          language={language}
          selectedKey={selectedMenuKey}
          menuAlignMode={menuAlignMode}
          menus={menus}
          collapsed={collapsed}
          openKeys={openMenuKeys}
          onOpenChange={(keys) => {
            if (!collapsed) {
              setSideMenuStore({
                openKeys: keys,
                beforeOpenKeys: keys,
              })
            }

            setOpenMenuKeys(keys)
          }}
          formatMenuPath={formatMenuPath}
          changeRouter={changeRouter}
          onBeforeChange={onBeforeMenuChange}
          onChange={onMenuChange}
          onSelect={onMenuSelect}
        />
        {collapseIconPlacement === 'bottom' && (
          <div className="tnt-layout-menu-collapse" onClick={collapseChangeHandle}>
            <Icon type={`menu-${collapsed ? 'unfold' : 'fold'}`} />
          </div>
        )}
      </Sider>
      <Content>
        <Header
          type={type}
          userInfo={userInfo}
          config={config}
          collapsed={collapsed}
          collapseIconPlacement={collapseIconPlacement}
          orgAppShow={orgAppShow}
          orgList={orgList}
          orgTreeConfig={orgTreeConfig}
          orgAppList={orgAppList}
          onOrgChange={onOrgChange}
          selectedOrgKey={selectedOrgKey}
          selectedAppKey={selectedAppKey}
          appList={appList}
          menus={menus}
          headerTabs={headerTabs}
          extraActions={extraHeaderActions}
          onAppChange={onAppChange}
          onCollapseChange={collapseChangeHandle}
          AvatarCustom={AvatarCustom}
          onThemeChange={(theme) => {
            setUserInfo({
              ...userInfo,
              theme,
            })
            onThemeChange && onThemeChange(theme)
          }}
          onLanguageChange={(lang) => {
            setUserInfo({
              ...userInfo,
              lang,
            })
            onLanguageChange && onLanguageChange(lang)
          }}
          onPersonalSetting={onPersonalSetting}
          onChangePassword={onChangePassword}
          onLogout={onLogout}
        >
          {headerNavs}
        </Header>
        {props.children}
      </Content>
    </TNTLayout>
  )
})
